import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const EscalationWorkflow = ({ selectedThreat }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [escalationData, setEscalationData] = useState({
    assessment: {
      threatLevel: '',
      impactScope: '',
      urgency: '',
      confidence: ''
    },
    stakeholders: {
      primaryContacts: [],
      secondaryContacts: [],
      externalAgencies: []
    },
    response: {
      immediateActions: [],
      mediumTermActions: [],
      communicationPlan: ''
    },
    briefing: {
      executiveSummary: '',
      keyFindings: '',
      recommendations: '',
      nextSteps: ''
    }
  });

  const escalationSteps = [
    {
      id: 1,
      title: 'Threat Assessment',
      description: 'Evaluate threat severity and impact scope',
      icon: 'Search',
      status: currentStep > 1 ? 'completed' : currentStep === 1 ? 'active' : 'pending'
    },
    {
      id: 2,
      title: 'Stakeholder Notification',
      description: 'Identify and notify relevant authorities',
      icon: 'Users',
      status: currentStep > 2 ? 'completed' : currentStep === 2 ? 'active' : 'pending'
    },
    {
      id: 3,
      title: 'Response Coordination',
      description: 'Coordinate multi-institutional response',
      icon: 'GitBranch',
      status: currentStep > 3 ? 'completed' : currentStep === 3 ? 'active' : 'pending'
    },
    {
      id: 4,
      title: 'Briefing Generation',
      description: 'Generate and distribute briefing materials',
      icon: 'FileText',
      status: currentStep > 4 ? 'completed' : currentStep === 4 ? 'active' : 'pending'
    }
  ];

  const stakeholderOptions = [
    { id: 'mofa', label: 'Ministry of Foreign Affairs', type: 'primary' },
    { id: 'moi', label: 'Ministry of Interior', type: 'primary' },
    { id: 'mod', label: 'Ministry of Defense', type: 'primary' },
    { id: 'efcsn', label: 'EFCSN Coordinators', type: 'secondary' },
    { id: 'edmo', label: 'EDMO Hub Leaders', type: 'secondary' },
    { id: 'europol', label: 'Europol', type: 'external' },
    { id: 'cert', label: 'National CERT Teams', type: 'external' }
  ];

  const responseActions = [
    { id: 'content-removal', label: 'Content Removal Requests', category: 'immediate' },
    { id: 'platform-notification', label: 'Platform Notification', category: 'immediate' },
    { id: 'public-alert', label: 'Public Alert System', category: 'immediate' },
    { id: 'fact-check-deployment', label: 'Fact-Check Deployment', category: 'medium' },
    { id: 'counter-narrative', label: 'Counter-Narrative Campaign', category: 'medium' },
    { id: 'legal-action', label: 'Legal Action Preparation', category: 'medium' }
  ];

  const getStepStatus = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-700 border-green-300';
      case 'active':
        return 'bg-blue-100 text-blue-700 border-blue-300';
      case 'pending':
        return 'bg-gray-100 text-gray-500 border-gray-300';
      default:
        return 'bg-gray-100 text-gray-500 border-gray-300';
    }
  };

  const handleStakeholderChange = (stakeholderId, checked) => {
    const stakeholder = stakeholderOptions?.find(s => s?.id === stakeholderId);
    if (!stakeholder) return;

    setEscalationData(prev => ({
      ...prev,
      stakeholders: {
        ...prev?.stakeholders,
        [`${stakeholder?.type}Contacts`]: checked
          ? [...prev?.stakeholders?.[`${stakeholder?.type}Contacts`], stakeholderId]
          : prev?.stakeholders?.[`${stakeholder?.type}Contacts`]?.filter(id => id !== stakeholderId)
      }
    }));
  };

  const handleActionChange = (actionId, checked) => {
    const action = responseActions?.find(a => a?.id === actionId);
    if (!action) return;

    const categoryKey = action?.category === 'immediate' ? 'immediateActions' : 'mediumTermActions';
    
    setEscalationData(prev => ({
      ...prev,
      response: {
        ...prev?.response,
        [categoryKey]: checked
          ? [...prev?.response?.[categoryKey], actionId]
          : prev?.response?.[categoryKey]?.filter(id => id !== actionId)
      }
    }));
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Threat Level</label>
                <select 
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  value={escalationData?.assessment?.threatLevel}
                  onChange={(e) => setEscalationData(prev => ({
                    ...prev,
                    assessment: { ...prev?.assessment, threatLevel: e?.target?.value }
                  }))}
                >
                  <option value="">Select threat level</option>
                  <option value="low">Low - Minimal Impact</option>
                  <option value="medium">Medium - Regional Impact</option>
                  <option value="high">High - National Impact</option>
                  <option value="critical">Critical - Cross-Border Impact</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Impact Scope</label>
                <select 
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  value={escalationData?.assessment?.impactScope}
                  onChange={(e) => setEscalationData(prev => ({
                    ...prev,
                    assessment: { ...prev?.assessment, impactScope: e?.target?.value }
                  }))}
                >
                  <option value="">Select impact scope</option>
                  <option value="local">Local - Single Region</option>
                  <option value="national">National - Single Country</option>
                  <option value="regional">Regional - Multiple Countries</option>
                  <option value="pan-eu">Pan-EU - Union-wide</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Urgency Level</label>
                <select 
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  value={escalationData?.assessment?.urgency}
                  onChange={(e) => setEscalationData(prev => ({
                    ...prev,
                    assessment: { ...prev?.assessment, urgency: e?.target?.value }
                  }))}
                >
                  <option value="">Select urgency</option>
                  <option value="routine">Routine - 24-48 hours</option>
                  <option value="priority">Priority - 4-8 hours</option>
                  <option value="urgent">Urgent - 1-2 hours</option>
                  <option value="immediate">Immediate - &lt;1 hour</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Confidence Level</label>
                <select 
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  value={escalationData?.assessment?.confidence}
                  onChange={(e) => setEscalationData(prev => ({
                    ...prev,
                    assessment: { ...prev?.assessment, confidence: e?.target?.value }
                  }))}
                >
                  <option value="">Select confidence</option>
                  <option value="low">Low - Requires verification</option>
                  <option value="medium">Medium - Likely accurate</option>
                  <option value="high">High - Confirmed sources</option>
                  <option value="verified">Verified - Multiple sources</option>
                </select>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3">Primary Contacts</h4>
                <div className="space-y-2">
                  {stakeholderOptions?.filter(s => s?.type === 'primary')?.map(stakeholder => (
                    <Checkbox
                      key={stakeholder?.id}
                      label={stakeholder?.label}
                      checked={escalationData?.stakeholders?.primaryContacts?.includes(stakeholder?.id)}
                      onChange={(e) => handleStakeholderChange(stakeholder?.id, e?.target?.checked)}
                    />
                  ))}
                </div>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3">Secondary Contacts</h4>
                <div className="space-y-2">
                  {stakeholderOptions?.filter(s => s?.type === 'secondary')?.map(stakeholder => (
                    <Checkbox
                      key={stakeholder?.id}
                      label={stakeholder?.label}
                      checked={escalationData?.stakeholders?.secondaryContacts?.includes(stakeholder?.id)}
                      onChange={(e) => handleStakeholderChange(stakeholder?.id, e?.target?.checked)}
                    />
                  ))}
                </div>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3">External Agencies</h4>
                <div className="space-y-2">
                  {stakeholderOptions?.filter(s => s?.type === 'external')?.map(stakeholder => (
                    <Checkbox
                      key={stakeholder?.id}
                      label={stakeholder?.label}
                      checked={escalationData?.stakeholders?.externalContacts?.includes(stakeholder?.id)}
                      onChange={(e) => handleStakeholderChange(stakeholder?.id, e?.target?.checked)}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3">Immediate Actions</h4>
                <div className="space-y-2">
                  {responseActions?.filter(a => a?.category === 'immediate')?.map(action => (
                    <Checkbox
                      key={action?.id}
                      label={action?.label}
                      checked={escalationData?.response?.immediateActions?.includes(action?.id)}
                      onChange={(e) => handleActionChange(action?.id, e?.target?.checked)}
                    />
                  ))}
                </div>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3">Medium-Term Actions</h4>
                <div className="space-y-2">
                  {responseActions?.filter(a => a?.category === 'medium')?.map(action => (
                    <Checkbox
                      key={action?.id}
                      label={action?.label}
                      checked={escalationData?.response?.mediumTermActions?.includes(action?.id)}
                      onChange={(e) => handleActionChange(action?.id, e?.target?.checked)}
                    />
                  ))}
                </div>
              </div>
            </div>
            <div>
              <Input
                label="Communication Plan"
                type="text"
                placeholder="Describe the communication strategy and key messages"
                value={escalationData?.response?.communicationPlan}
                onChange={(e) => setEscalationData(prev => ({
                  ...prev,
                  response: { ...prev?.response, communicationPlan: e?.target?.value }
                }))}
                className="w-full"
              />
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 gap-4">
              <Input
                label="Executive Summary"
                type="text"
                placeholder="Brief overview of the threat and response"
                value={escalationData?.briefing?.executiveSummary}
                onChange={(e) => setEscalationData(prev => ({
                  ...prev,
                  briefing: { ...prev?.briefing, executiveSummary: e?.target?.value }
                }))}
              />
              
              <Input
                label="Key Findings"
                type="text"
                placeholder="Main findings from the threat analysis"
                value={escalationData?.briefing?.keyFindings}
                onChange={(e) => setEscalationData(prev => ({
                  ...prev,
                  briefing: { ...prev?.briefing, keyFindings: e?.target?.value }
                }))}
              />
              
              <Input
                label="Recommendations"
                type="text"
                placeholder="Recommended actions and strategies"
                value={escalationData?.briefing?.recommendations}
                onChange={(e) => setEscalationData(prev => ({
                  ...prev,
                  briefing: { ...prev?.briefing, recommendations: e?.target?.value }
                }))}
              />
              
              <Input
                label="Next Steps"
                type="text"
                placeholder="Planned follow-up actions and timeline"
                value={escalationData?.briefing?.nextSteps}
                onChange={(e) => setEscalationData(prev => ({
                  ...prev,
                  briefing: { ...prev?.briefing, nextSteps: e?.target?.value }
                }))}
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (!selectedThreat) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-8 text-center">
        <Icon name="AlertTriangle" size={48} className="text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Threat Selected</h3>
        <p className="text-gray-500">Select a threat from the monitoring dashboard to begin escalation workflow.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-gray-200">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Escalation Workflow</h2>
          <p className="text-sm text-gray-500">Threat ID: {selectedThreat?.id}</p>
        </div>
        
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-500">Step {currentStep} of {escalationSteps?.length}</span>
          <div className="w-24 bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(currentStep / escalationSteps?.length) * 100}%` }}
            />
          </div>
        </div>
      </div>
      {/* Progress Steps */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          {escalationSteps?.map((step, index) => (
            <div key={step?.id} className="flex items-center">
              <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${getStepStatus(step?.status)}`}>
                <Icon name={step?.icon} size={18} />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900">{step?.title}</p>
                <p className="text-xs text-gray-500">{step?.description}</p>
              </div>
              {index < escalationSteps?.length - 1 && (
                <div className="flex-1 mx-4 h-0.5 bg-gray-200">
                  <div 
                    className={`h-full transition-all duration-300 ${
                      step?.status === 'completed' ? 'bg-green-500' : 'bg-gray-200'
                    }`}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      {/* Step Content */}
      <div className="p-6">
        <div className="mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {escalationSteps?.[currentStep - 1]?.title}
          </h3>
          <p className="text-sm text-gray-600">
            {escalationSteps?.[currentStep - 1]?.description}
          </p>
        </div>

        {renderStepContent()}

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
          <Button
            variant="outline"
            onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
            disabled={currentStep === 1}
            iconName="ChevronLeft"
            iconPosition="left"
          >
            Previous
          </Button>
          
          <div className="flex items-center space-x-3">
            {currentStep === escalationSteps?.length ? (
              <Button
                variant="default"
                iconName="Send"
                iconPosition="left"
                onClick={() => {
                  // Generate and distribute briefing
                  alert('Briefing generated and distributed to selected stakeholders');
                }}
              >
                Generate & Distribute Briefing
              </Button>
            ) : (
              <Button
                variant="default"
                onClick={() => setCurrentStep(Math.min(escalationSteps?.length, currentStep + 1))}
                iconName="ChevronRight"
                iconPosition="right"
              >
                Next
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EscalationWorkflow;